import React, { useState, useEffect } from 'react';
import { BookOpen, Plus, Search, Folder, FileText, Trash2, Edit2, X, Save, ChevronRight, ArrowLeft } from 'lucide-react';
import { useAuth } from '../contexts/AuthContext';
import Layout from '../components/Layout';

const StudyNotes = () => {
  const { currentUser } = useAuth();
  const [folders, setFolders] = useState([]);
  const [notes, setNotes] = useState([]);
  const [currentFolder, setCurrentFolder] = useState(null);
  const [searchTerm, setSearchTerm] = useState('');
  const [showFolderModal, setShowFolderModal] = useState(false);
  const [showNoteModal, setShowNoteModal] = useState(false);
  const [editingFolder, setEditingFolder] = useState(null);
  const [editingNote, setEditingNote] = useState(null);
  const [folderName, setFolderName] = useState('');
  const [noteData, setNoteData] = useState({
    title: '',
    content: '',
    folderId: null
  });

  const defaultFolders = [
    { id: 'anatomy', name: '🦴 Anatomy', color: 'bg-blue-100 border-blue-300' },
    { id: 'medications', name: '💊 Medications', color: 'bg-green-100 border-green-300' },
    { id: 'procedures', name: '🩺 Procedures', color: 'bg-purple-100 border-purple-300' },
    { id: 'diseases', name: '🦠 Diseases', color: 'bg-red-100 border-red-300' },
    { id: 'lab', name: '🔬 Lab & Diagnostics', color: 'bg-yellow-100 border-yellow-300' },
    { id: 'behavior', name: '🐕 Animal Behavior', color: 'bg-orange-100 border-orange-300' },
  ];

  // Load data from localStorage
  useEffect(() => {
    if (currentUser) {
      const savedFolders = localStorage.getItem(`studyFolders_${currentUser.uid}`);
      const savedNotes = localStorage.getItem(`studyNotes_${currentUser.uid}`);
      
      if (savedFolders) {
        setFolders(JSON.parse(savedFolders));
      } else {
        // Initialize with default folders
        setFolders(defaultFolders);
      }
      
      if (savedNotes) {
        setNotes(JSON.parse(savedNotes));
      }
    }
  }, [currentUser]);

  // Save to localStorage
  useEffect(() => {
    if (currentUser) {
      localStorage.setItem(`studyFolders_${currentUser.uid}`, JSON.stringify(folders));
      localStorage.setItem(`studyNotes_${currentUser.uid}`, JSON.stringify(notes));
    }
  }, [folders, notes, currentUser]);

  const openAddFolder = () => {
    setFolderName('');
    setEditingFolder(null);
    setShowFolderModal(true);
  };

  const openEditFolder = (folder, e) => {
    e.stopPropagation();
    setFolderName(folder.name);
    setEditingFolder(folder);
    setShowFolderModal(true);
  };

  const saveFolder = () => {
    if (!folderName.trim()) return;

    if (editingFolder) {
      setFolders(prev => prev.map(f => 
        f.id === editingFolder.id ? { ...f, name: folderName } : f
      ));
    } else {
      const newFolder = {
        id: Date.now().toString(),
        name: folderName,
        color: 'bg-gray-100 border-gray-300'
      };
      setFolders(prev => [...prev, newFolder]);
    }

    setShowFolderModal(false);
    setFolderName('');
    setEditingFolder(null);
  };

  const deleteFolder = (folderId, e) => {
    e.stopPropagation();
    if (window.confirm('Delete this folder and all its notes?')) {
      setFolders(prev => prev.filter(f => f.id !== folderId));
      setNotes(prev => prev.filter(n => n.folderId !== folderId));
      if (currentFolder?.id === folderId) {
        setCurrentFolder(null);
      }
    }
  };

  const openAddNote = () => {
    setNoteData({
      title: '',
      content: '',
      folderId: currentFolder?.id || null
    });
    setEditingNote(null);
    setShowNoteModal(true);
  };

  const openEditNote = (note) => {
    setNoteData({
      title: note.title,
      content: note.content,
      folderId: note.folderId
    });
    setEditingNote(note);
    setShowNoteModal(true);
  };

  const saveNote = () => {
    if (!noteData.title.trim()) {
      alert('Please enter a title');
      return;
    }

    if (editingNote) {
      setNotes(prev => prev.map(n =>
        n.id === editingNote.id
          ? { ...n, ...noteData, updatedAt: new Date().toISOString() }
          : n
      ));
    } else {
      const newNote = {
        id: Date.now(),
        ...noteData,
        createdAt: new Date().toISOString(),
        updatedAt: new Date().toISOString()
      };
      setNotes(prev => [newNote, ...prev]);
    }

    setShowNoteModal(false);
    setNoteData({ title: '', content: '', folderId: null });
    setEditingNote(null);
  };

  const deleteNote = (noteId) => {
    if (window.confirm('Delete this note?')) {
      setNotes(prev => prev.filter(n => n.id !== noteId));
    }
  };

  const getNotesForFolder = (folderId) => {
    return notes.filter(n => n.folderId === folderId);
  };

  const getFilteredNotes = () => {
    let filtered = currentFolder 
      ? notes.filter(n => n.folderId === currentFolder.id)
      : notes;

    if (searchTerm) {
      const search = searchTerm.toLowerCase();
      filtered = filtered.filter(n =>
        n.title.toLowerCase().includes(search) ||
        n.content.toLowerCase().includes(search)
      );
    }

    return filtered.sort((a, b) => new Date(b.updatedAt) - new Date(a.updatedAt));
  };

  const formatDate = (dateString) => {
    return new Date(dateString).toLocaleDateString('en-US', {
      month: 'short',
      day: 'numeric',
      year: 'numeric'
    });
  };

  const getFolderById = (id) => folders.find(f => f.id === id);

  return (
    <Layout>
      <div className="max-w-4xl mx-auto">
        {/* Header */}
        <div className="mb-6">
          <h1 className="text-2xl md:text-3xl font-bold text-brand-brown flex items-center">
            <BookOpen className="w-8 h-8 mr-3 text-pastel-purple" />
            Study Notes
          </h1>
          <p className="text-brand-brown/60 mt-1">Keep track of what you're learning</p>
        </div>

        {/* Breadcrumb & Search */}
        <div className="flex flex-col sm:flex-row sm:items-center space-y-3 sm:space-y-0 sm:space-x-3 mb-6">
          {currentFolder ? (
            <button
              onClick={() => setCurrentFolder(null)}
              className="flex items-center text-pastel-purple hover:text-brand-brown"
            >
              <ArrowLeft className="w-5 h-5 mr-1" />
              Back to Folders
            </button>
          ) : (
            <div className="flex items-center text-brand-brown/60">
              <Folder className="w-5 h-5 mr-2" />
              All Folders
            </div>
          )}

          <div className="flex-1 relative">
            <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-5 h-5 text-brand-brown/40" />
            <input
              type="text"
              placeholder="Search notes..."
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              className="input pl-10 w-full"
            />
          </div>
        </div>

        {/* Folders View */}
        {!currentFolder && !searchTerm && (
          <div className="mb-6">
            <div className="flex items-center justify-between mb-3">
              <h2 className="font-semibold text-brand-brown">Folders</h2>
              <button
                onClick={openAddFolder}
                className="text-sm text-pastel-purple hover:text-brand-brown flex items-center"
              >
                <Plus className="w-4 h-4 mr-1" />
                New Folder
              </button>
            </div>

            <div className="grid grid-cols-2 sm:grid-cols-3 gap-3">
              {folders.map(folder => (
                <div
                  key={folder.id}
                  onClick={() => setCurrentFolder(folder)}
                  className={`card p-4 border-2 cursor-pointer hover:shadow-md transition-all ${folder.color}`}
                >
                  <div className="flex items-start justify-between">
                    <div className="flex-1 min-w-0">
                      <h3 className="font-medium text-brand-brown truncate">{folder.name}</h3>
                      <p className="text-xs text-brand-brown/60 mt-1">
                        {getNotesForFolder(folder.id).length} notes
                      </p>
                    </div>
                    <div className="flex items-center space-x-1 ml-2">
                      <button
                        onClick={(e) => openEditFolder(folder, e)}
                        className="p-1 hover:bg-white/50 rounded"
                      >
                        <Edit2 className="w-4 h-4 text-brand-brown/40" />
                      </button>
                      <button
                        onClick={(e) => deleteFolder(folder.id, e)}
                        className="p-1 hover:bg-red-100 rounded"
                      >
                        <Trash2 className="w-4 h-4 text-brand-brown/40 hover:text-red-500" />
                      </button>
                    </div>
                  </div>
                </div>
              ))}
            </div>
          </div>
        )}

        {/* Current Folder Header */}
        {currentFolder && (
          <div className={`card p-4 mb-4 border-2 ${currentFolder.color}`}>
            <div className="flex items-center justify-between">
              <h2 className="font-bold text-brand-brown text-lg">{currentFolder.name}</h2>
              <button
                onClick={openAddNote}
                className="btn-primary flex items-center px-3 py-2 text-sm"
              >
                <Plus className="w-4 h-4 mr-1" />
                Add Note
              </button>
            </div>
          </div>
        )}

        {/* Notes List */}
        <div className="space-y-3">
          {(currentFolder || searchTerm) && (
            <>
              {getFilteredNotes().length === 0 ? (
                <div className="card p-8 text-center">
                  <FileText className="w-12 h-12 mx-auto text-brand-brown/20 mb-3" />
                  <p className="text-brand-brown/60">
                    {searchTerm ? 'No matching notes found' : 'No notes yet. Add one to get started!'}
                  </p>
                  {!searchTerm && (
                    <button
                      onClick={openAddNote}
                      className="btn-primary mt-4 px-6"
                    >
                      <Plus className="w-4 h-4 mr-1 inline" />
                      Add Note
                    </button>
                  )}
                </div>
              ) : (
                getFilteredNotes().map(note => (
                  <div key={note.id} className="card p-4">
                    <div className="flex items-start justify-between mb-2">
                      <div className="flex-1 min-w-0">
                        <h3 className="font-semibold text-brand-brown">{note.title}</h3>
                        {searchTerm && note.folderId && (
                          <p className="text-xs text-pastel-purple mt-0.5">
                            in {getFolderById(note.folderId)?.name || 'Unknown'}
                          </p>
                        )}
                      </div>
                      <div className="flex items-center space-x-1 ml-2">
                        <button
                          onClick={() => openEditNote(note)}
                          className="p-2 hover:bg-pastel-blue/20 rounded-lg"
                        >
                          <Edit2 className="w-4 h-4 text-brand-brown/60" />
                        </button>
                        <button
                          onClick={() => deleteNote(note.id)}
                          className="p-2 hover:bg-red-100 rounded-lg"
                        >
                          <Trash2 className="w-4 h-4 text-brand-brown/40 hover:text-red-500" />
                        </button>
                      </div>
                    </div>
                    
                    {note.content && (
                      <p className="text-brand-brown/70 text-sm whitespace-pre-wrap line-clamp-3">
                        {note.content}
                      </p>
                    )}
                    
                    <p className="text-xs text-brand-brown/40 mt-3">
                      Updated {formatDate(note.updatedAt)}
                    </p>
                  </div>
                ))
              )}
            </>
          )}

          {/* Recent Notes (on home view) */}
          {!currentFolder && !searchTerm && notes.length > 0 && (
            <div>
              <h2 className="font-semibold text-brand-brown mb-3">Recent Notes</h2>
              {notes.slice(0, 5).map(note => (
                <div
                  key={note.id}
                  onClick={() => openEditNote(note)}
                  className="card p-3 mb-2 cursor-pointer hover:shadow-md transition-all"
                >
                  <div className="flex items-center justify-between">
                    <div className="flex-1 min-w-0">
                      <h3 className="font-medium text-brand-brown truncate">{note.title}</h3>
                      <p className="text-xs text-brand-brown/60">
                        {getFolderById(note.folderId)?.name || 'Unfiled'} • {formatDate(note.updatedAt)}
                      </p>
                    </div>
                    <ChevronRight className="w-5 h-5 text-brand-brown/30" />
                  </div>
                </div>
              ))}
            </div>
          )}
        </div>

        {/* Folder Modal */}
        {showFolderModal && (
          <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4">
            <div className="bg-white rounded-2xl w-full max-w-sm p-6">
              <div className="flex items-center justify-between mb-4">
                <h2 className="text-xl font-bold text-brand-brown">
                  {editingFolder ? 'Edit Folder' : 'New Folder'}
                </h2>
                <button
                  onClick={() => setShowFolderModal(false)}
                  className="p-2 hover:bg-gray-100 rounded-full"
                >
                  <X className="w-5 h-5" />
                </button>
              </div>

              <input
                type="text"
                value={folderName}
                onChange={(e) => setFolderName(e.target.value)}
                placeholder="Folder name (e.g., 🐱 Feline Health)"
                className="input w-full mb-4"
                autoFocus
              />

              <p className="text-xs text-brand-brown/60 mb-4">
                Tip: Add an emoji at the start for visual organization!
              </p>

              <button
                onClick={saveFolder}
                disabled={!folderName.trim()}
                className="btn-primary w-full py-3 disabled:opacity-50"
              >
                {editingFolder ? 'Save Changes' : 'Create Folder'}
              </button>
            </div>
          </div>
        )}

        {/* Note Modal */}
        {showNoteModal && (
          <div className="fixed inset-0 bg-black/50 flex items-end sm:items-center justify-center z-50 p-4">
            <div className="bg-white rounded-t-2xl sm:rounded-2xl w-full max-w-lg max-h-[90vh] overflow-y-auto">
              <div className="sticky top-0 bg-white p-4 border-b flex items-center justify-between">
                <h2 className="text-xl font-bold text-brand-brown">
                  {editingNote ? 'Edit Note' : 'New Note'}
                </h2>
                <button
                  onClick={() => setShowNoteModal(false)}
                  className="p-2 hover:bg-gray-100 rounded-full"
                >
                  <X className="w-5 h-5" />
                </button>
              </div>

              <div className="p-4 space-y-4">
                <div>
                  <label className="block text-sm font-medium text-brand-brown mb-1">Title *</label>
                  <input
                    type="text"
                    value={noteData.title}
                    onChange={(e) => setNoteData(prev => ({ ...prev, title: e.target.value }))}
                    placeholder="e.g., Canine Heart Anatomy"
                    className="input w-full"
                  />
                </div>

                <div>
                  <label className="block text-sm font-medium text-brand-brown mb-1">Folder</label>
                  <select
                    value={noteData.folderId || ''}
                    onChange={(e) => setNoteData(prev => ({ ...prev, folderId: e.target.value || null }))}
                    className="input w-full"
                  >
                    <option value="">No folder</option>
                    {folders.map(folder => (
                      <option key={folder.id} value={folder.id}>{folder.name}</option>
                    ))}
                  </select>
                </div>

                <div>
                  <label className="block text-sm font-medium text-brand-brown mb-1">Content</label>
                  <textarea
                    value={noteData.content}
                    onChange={(e) => setNoteData(prev => ({ ...prev, content: e.target.value }))}
                    placeholder="Write your notes here...&#10;&#10;• Key points&#10;• Important details&#10;• Things to remember"
                    className="input w-full h-48 resize-none font-mono text-sm"
                  />
                </div>

                <button
                  onClick={saveNote}
                  className="btn-primary w-full py-3 flex items-center justify-center"
                >
                  <Save className="w-5 h-5 mr-2" />
                  {editingNote ? 'Save Changes' : 'Create Note'}
                </button>
              </div>
            </div>
          </div>
        )}
      </div>
    </Layout>
  );
};

export default StudyNotes;
